<script setup>
import OpenShiftDialog from '@/components/dialogs/OpenShiftDialog.vue'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Sales',
  },
})

const ability = useAbility()
const { formatRupiah, formatDate } = useFormat()
const isLoading = ref(false)

// ── State ──────────────────────────────────────────────────────────────
const shifts = ref([])
const totalItems = ref(0)
const options = ref({ page: 1, itemsPerPage: 15, sortBy: [] })
const statusFilter = ref(null)

const closeShiftDialog = ref(false)
const selectedShift = ref(null)
const detailDialog = ref(false)
const detailLoading = ref(false)
const detailShift = ref(null)

const openShiftDialog = ref(false)

const statusOptions = [
  { title: 'Semua Status', value: null },
  { title: 'Sedang Berjalan', value: 'open' },
  { title: 'Ditutup', value: 'closed' },
]

const headers = [
  { title: 'Kasir', key: 'cashier.name' },
  { title: 'Status', key: 'status' },
  { title: 'Kas Awal', key: 'opening_cash' },
  { title: 'Kas Akhir', key: 'closing_cash' },
  { title: 'Selisih', key: 'difference' },
  { title: 'Dibuka', key: 'opened_at' },
  { title: 'Ditutup', key: 'closed_at' },
  { title: 'Aksi', key: 'actions', sortable: false, width: '100px' },
]

// ── Fetch ──────────────────────────────────────────────────────────────
const fetchShifts = async () => {
  isLoading.value = true
  try {
    const response = await useApi('/api/v1/shifts', {
      query: {
        page: options.value.page,
        per_page: options.value.itemsPerPage,
        status: statusFilter.value || undefined,
      },
    }).get()
    const data = response?.data ?? response

    shifts.value = data?.data ?? []
    totalItems.value = data?.meta?.total ?? 0
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchShifts)
watch(options, fetchShifts, { deep: true })
watch(statusFilter, fetchShifts)

// ── Detail ─────────────────────────────────────────────────────────────
const viewDetail = async shift => {
  detailDialog.value = true
  detailLoading.value = true
  try {
    const response = await useApi(`/api/v1/shifts/${shift.id}`).get()
    detailShift.value = response?.data ?? response
  } finally {
    detailLoading.value = false
  }
}

// ── Close shift (owner/manager bisa tutup shift siapapun) ──────────────
const openCloseDialog = shift => {
  selectedShift.value = shift
  closeShiftDialog.value = true
}

const handleShiftClosed = () => {
  fetchShifts()
}

// ✅ Handler setelah shift berhasil dibuka
const handleShiftOpened = () => {
  fetchShifts()
}
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Shift Kasir</h4>
        <p class="text-body-2 text-medium-emphasis">
          Riwayat dan status shift kasir di toko Anda
        </p>
      </VCol>
    </VRow>

    <VCard>
      <VCardText class="d-flex align-center gap-3 flex-wrap">
        <VSelect
          v-model="statusFilter"
          :items="statusOptions"
          item-title="title"
          item-value="value"
          label="Filter Status"
          density="compact"
          hide-details
          style="max-width: 220px"
        />
        <VSpacer />
        
        <!-- ✅ Tombol Buka Shift -->
        <VBtn
          v-if="ability.can('create', 'Sales')"
          color="success"
          prepend-icon="ri-lock-unlock-line"
          @click="openShiftDialog = true"
        >
          Buka Shift
        </VBtn>
        
        <!-- Tombol Masuk POS -->
        <VBtn
          v-if="ability.can('create', 'Sales')"
          :to="{ name: 'pos' }"
          color="primary"
          prepend-icon="ri-shopping-cart-line"
        >
          Masuk POS
        </VBtn>
      </VCardText>
      
      <VDivider />
      
      <VDataTable
        v-model:options="options"
        :headers="headers"
        :items="shifts"
        :loading="isLoading"
        :items-length="totalItems"
        hover
      >
        <template #item.cashier.name="{ item }">
          {{ item.cashier?.name ?? '-' }}
        </template>
        <template #item.status="{ item }">
          <VChip
            :color="item.status === 'open' ? 'success' : 'default'"
            size="small"
            variant="tonal"
          >
            {{ item.status === 'open' ? 'Berjalan' : 'Ditutup' }}
          </VChip>
        </template>
        <template #item.opening_cash="{ item }">
          {{ formatRupiah(item.opening_cash) }}
        </template>
        <template #item.closing_cash="{ item }">
          {{ item.closing_cash !== null ? formatRupiah(item.closing_cash) : '-' }}
        </template>
        <template #item.difference="{ item }">
          <span
            v-if="item.difference !== null"
            :class="item.difference === 0 ? 'text-success' : item.difference < 0 ? 'text-error' : 'text-info'"
          >
            {{ item.difference > 0 ? '+' : '' }}{{ formatRupiah(item.difference) }}
          </span>
          <span v-else>-</span>
        </template>
        <template #item.opened_at="{ item }">
          {{ formatDate(item.opened_at, 'DD MMM YYYY HH:mm') }}
        </template>
        <template #item.closed_at="{ item }">
          {{ item.closed_at ? formatDate(item.closed_at, 'DD MMM YYYY HH:mm') : '-' }}
        </template>
        <template #item.actions="{ item }">
          <VBtn
            icon
            size="small"
            variant="text"
            @click="viewDetail(item)"
          >
            <VIcon icon="ri-eye-line" />
          </VBtn>
          <VBtn
            v-if="item.status === 'open' && ability.can('update', 'Sales')"
            icon
            size="small"
            variant="text"
            color="warning"
            @click="openCloseDialog(item)"
          >
            <VIcon icon="ri-lock-line" />
            <VTooltip activator="parent">Tutup Shift</VTooltip>
          </VBtn>
        </template>
        <template #no-data>
          <div class="text-center pa-8">
            <VIcon icon="ri-time-line" size="64" color="grey-lighten-1" />
            <p class="text-body-1 mt-4">Belum ada riwayat shift</p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <!-- Detail Dialog -->
    <VDialog v-model="detailDialog" max-width="500">
      <VCard>
        <VCardTitle class="pa-4">Detail Shift</VCardTitle>
        <VDivider />
        <VCardText class="pa-4">
          <div v-if="detailLoading" class="d-flex justify-center pa-4">
            <VProgressCircular indeterminate color="primary" />
          </div>
          <div v-else-if="detailShift">
            <div class="d-flex justify-space-between text-body-2 mb-2">
              <span>Kasir</span>
              <span class="font-weight-medium">{{ detailShift.cashier?.name }}</span>
            </div>
            <div class="d-flex justify-space-between text-body-2 mb-2">
              <span>Status</span>
              <VChip
                :color="detailShift.status === 'open' ? 'success' : 'default'"
                size="small"
                variant="tonal"
              >
                {{ detailShift.status === 'open' ? 'Berjalan' : 'Ditutup' }}
              </VChip>
            </div>
            <VDivider class="my-2" />
            <div class="d-flex justify-space-between text-body-2 mb-2">
              <span>Kas Awal</span>
              <span>{{ formatRupiah(detailShift.opening_cash) }}</span>
            </div>
            <div
              v-if="detailShift.closing_cash !== null"
              class="d-flex justify-space-between text-body-2 mb-2"
            >
              <span>Kas Akhir</span>
              <span>{{ formatRupiah(detailShift.closing_cash) }}</span>
            </div>
            <div
              v-if="detailShift.expected_cash !== null"
              class="d-flex justify-space-between text-body-2 mb-2"
            >
              <span>Kas Seharusnya</span>
              <span>{{ formatRupiah(detailShift.expected_cash) }}</span>
            </div>
            <div
              v-if="detailShift.difference !== null"
              class="d-flex justify-space-between text-body-2 mb-2"
            >
              <span>Selisih</span>
              <span :class="detailShift.difference === 0 ? 'text-success' : (detailShift.difference < 0 ? 'text-error' : 'text-info')">
                {{ formatRupiah(detailShift.difference) }}
              </span>
            </div>
            <VDivider v-if="detailShift.notes" class="my-2" />
            <div v-if="detailShift.notes" class="text-body-2">
              <strong>Catatan:</strong> {{ detailShift.notes }}
            </div>
          </div>
        </VCardText>
        <VDivider />
        <VCardActions class="pa-4">
          <VSpacer />
          <VBtn variant="text" @click="detailDialog = false">Tutup</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Close Shift Dialog -->
    <CloseShiftDialog
      v-model="closeShiftDialog"
      :shift="selectedShift"
      @closed="handleShiftClosed"
    />

    <!-- ✅ Open Shift Dialog -->
    <OpenShiftDialog
      v-model="openShiftDialog"
      @opened="handleShiftOpened"
    />
  </div>
</template>
